import React, { useEffect, useState, useRef } from 'react';
import { CommonTable } from '../../components/CommonTable';
import { Button } from '../../components/ui/Button';
import { Plus } from 'lucide-react';
import BucketDetailsDialog from './BucketDetails';
import AddBucketDialog from './AddBucket';
import { useAppSelector, useAppDispatch } from '../../store/hooks/reduxHooks';
import { SchemeBucket } from '../../services/schemeBucket';
import { fetchBucketsThunk, setPage, setPageSize, setSearchQuery, setSortBy, setSortOrder, setHasFetched } from '../../features/schemeBuckets/schemeBucketSlice';

const ManageSchemeBucket: React.FC = () => {
  const dispatch = useAppDispatch();
  const { buckets, loading, page, pageSize, total, searchQuery, hasFetched, sortBy, sortOrder } = useAppSelector(
    (state) => state.schemeBucket
  );

  const [selectedBucketId, setBucketId] = useState<string | null>(null);
  const [selectedBucketName, setBucketName] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { token } = useAppSelector((state) => state.auth);

  const prevRef = useRef({
    page,
    pageSize,
    sortBy,
    sortOrder,
    searchQuery,
  });

  // ✅ Only fetch once on first mount
  useEffect(() => {
    if (!hasFetched) return;

    const prev = prevRef.current;

    const hasChanged =
      prev.searchQuery !== searchQuery ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortOrder !== sortOrder;

    if (!hasChanged) return;

    const timeout = setTimeout(() => {
      dispatch(fetchBucketsThunk());

      // 🔑 update snapshot AFTER dispatch
      prevRef.current = {
        searchQuery,
        page,
        pageSize,
        sortBy,
        sortOrder,
      };
    }, 500);

    return () => clearTimeout(timeout);
  }, [
    searchQuery,
    page,
    pageSize,
    sortBy,
    sortOrder,
    hasFetched,
    dispatch,
  ]);



  useEffect(() => {
    if (!token) return;
    if (hasFetched) return;


    dispatch(fetchBucketsThunk());

  }, [token, hasFetched, dispatch]);
  const handleRefresh = () => {
     dispatch(setPage(1));
    dispatch(fetchBucketsThunk());
  };

  const columns = [
    {
      id: 'srNo',
      header: 'Sr. No',
      cell: ({ row }: any) => (page - 1) * pageSize + row.index + 1,
      size: 60,
    },
    {
      id: 'name',
      header: 'Scheme Bucket Name',
      accessorKey: 'name',
      size: 120,
    },
    {
      id: 'schemeBucketId',
      header: 'Scheme Bucket ID',
      accessorKey: 'schemeBucketId',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      size: 180,
      cell: ({ row }: any) =>
        new Date(row.original.createdAt).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        }),
    },
  ];

  return (
    <div className="w-full no-scrollbar">
      <div className="flex justify-end items-center mb-4 mr-1">
        <Button onClick={() => setShowAddDialog(true)} className="flex items-center gap-1" size="sm">
          <Plus size={14} />
          <span>Add Scheme Bucket</span>
        </Button>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <CommonTable
          columns={columns}
          data={buckets}
          isLoading={loading}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={(p) => dispatch(setPage(p))}
          onPageSizeChange={(size) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setPageSize(size));
          }}
          onSortChange={(field, order) => {
            dispatch(setPage(1));          // 🔑 ADD THIS
            dispatch(setSortBy(field));
            dispatch(setSortOrder(order));
          }}

          onSearchChange={(value) => {
            if (value !== searchQuery) {
              dispatch(setPage(1));
              dispatch(setSearchQuery(value));
            }
          }}
          searchPlaceholder="Search Buckets..."
          enableRowClick={true}
          onRefresh={handleRefresh} // <-- Add this prop!
          onRowClick={(row: SchemeBucket) => {
            setBucketId(row.schemeBucketId!);
            setShowDialog(true);
            setBucketName(row.name);
          }}
        />

        <BucketDetailsDialog
          bucketId={selectedBucketId}
          bucketName={selectedBucketName}
          isOpen={showDialog}
          onClose={() => setShowDialog(false)}
        />

        <AddBucketDialog
          isOpen={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onAdded={handleRefresh}
        />
      </div>
    </div>
  );
};

export default ManageSchemeBucket;
